import React, { ReactNode, createContext, useContext, useReducer } from 'react';
import { ToastStatus } from '@wix/design-system/dist/types/StatusToast/StatusToast.types';

interface ToastProps {
  show: boolean;
  status: ToastStatus;
  message: string;
}

interface ModalsContextProviderProps {
  children?: ReactNode;
  host: string;
  showReviewModal?: boolean;
  showCancelModal?: boolean;
  showPartnerDisputeModal?: boolean;
  toast?: ToastProps;
}

type State = {
  host: string;
  showReviewModal: boolean;
  showCancelModal: boolean;
  showPartnerDisputeModal: boolean;
  toast: ToastProps;
};

interface ModalsContextType extends State {
  dispatchModal: (arg: ModalAction) => void;
}

const ModalsContext = createContext<ModalsContextType | null>(null);

type ModalAction = {
  message?: string;
  showCancelModal?: boolean;
  showReviewModal?: boolean;
  showPartnerDisputeModal?: boolean;
  type: (typeof MODAL_ACTIONS)[keyof typeof MODAL_ACTIONS];
};

function mainReducer(state: State, action: ModalAction) {
  switch (action.type) {
    case MODAL_ACTIONS.SHOW_REVIEW_MODAL: {
      return {
        ...state,
        showReviewModal: true,
      };
    }
    case MODAL_ACTIONS.HIDE_REVIEW_MODAL: {
      return {
        ...state,
        showReviewModal: false,
      };
    }
    case MODAL_ACTIONS.SHOW_PARTNER_DISPUTE_MODAL: {
      return {
        ...state,
        showPartnerDisputeModal: true,
      };
    }
    case MODAL_ACTIONS.HIDE_PARTNER_DISPUTE_MODAL: {
      return {
        ...state,
        showPartnerDisputeModal: false,
      };
    }
    case MODAL_ACTIONS.SHOW_CANCEL_MODAL: {
      return {
        ...state,
        showCancelModal: true,
      };
    }
    case MODAL_ACTIONS.HIDE_CANCEL_MODAL: {
      return {
        ...state,
        showCancelModal: false,
      };
    }
    case MODAL_ACTIONS.SHOW_ERROR_TOAST: {
      const { message } = action;
      return {
        ...state,
        toast: {
          status: 'error',
          show: true,
          message,
        } as ToastProps,
      };
    }
    case MODAL_ACTIONS.HIDE_TOAST: {
      return {
        ...state,
        toast: {
          ...state.toast,
          show: false,
        } as ToastProps,
      };
    }
    default:
      throw new Error();
  }
}

export const MODAL_ACTIONS = {
  SHOW_REVIEW_MODAL: 'SHOW_REVIEW_MODAL',
  HIDE_REVIEW_MODAL: 'HIDE_REVIEW_MODAL',
  SHOW_CANCEL_MODAL: 'SHOW_CANCEL_MODAL',
  HIDE_CANCEL_MODAL: 'HIDE_CANCEL_MODAL',
  SHOW_ERROR_TOAST: 'SHOW_ERROR_TOAST',
  HIDE_TOAST: 'HIDE_TOAST',
  SHOW_PARTNER_DISPUTE_MODAL: 'SHOW_PARTNER_DISPUTE_MODAL',
  HIDE_PARTNER_DISPUTE_MODAL: 'HIDE_PARTNER_DISPUTE_MODAL',
};

export function ModalsContextProvider(props: ModalsContextProviderProps) {
  const [state, dispatchModal] = useReducer(mainReducer, {
    host: props.host,
    showReviewModal: props.showReviewModal || false,
    showCancelModal: props.showCancelModal || false,
    showPartnerDisputeModal: Boolean(props.showPartnerDisputeModal),
    toast: { status: 'error', show: false, message: '' },
  });

  return (
    <ModalsContext.Provider
      value={{
        ...state,
        dispatchModal,
      }}
    >
      {props.children}
    </ModalsContext.Provider>
  );
}

export function useModalsContext() {
  const appContext = useContext(ModalsContext);
  if (!appContext) {
    throw new Error(
      'useModalsContext must be used within the ModalsContext.Provider',
    );
  }
  return appContext;
}
